// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useCallback, useEffect, useState } from 'react';
import { ActionableNotification, Button, InlineLoading, InlineNotification } from '@carbon/react';
import UploadDropzone, { type UploadingFile } from '../components/UploadDropzone';
import ChecklistStatTiles from '../components/ChecklistStatTiles';
import ChecklistTable from '../components/ChecklistTable';
import BypassModal from '../components/BypassModal';
import ReviewMatchModal from '../components/ReviewMatchModal';
import { useCoverageChecklist } from '../../../hooks/useCoverageChecklist';
import { bypassItem, removeBypass, submitSession } from '../../../services/coverageService';
import {
  streamOnboardingRun,
  streamOnboardingResume,
  type GatePayload,
  type OnboardingFilePayload,
} from '../../../services/onboardingService';
import { uploadStoreObject } from '../../../services/objectStoreService';
import { getUserData } from '../../../services/tokenStorage';
import type { ChecklistItem, CoverageSession } from '../../../types';

// A made-up tenant on the BFF's deprecated tenant routes: workarounds 4 and 5 in
// docs/design/obo-to-s2s/object-store-bff-workarounds.md.
const ONBOARDING_TENANT = 'helios-onboarding';

function sanitizeKey(filename: string): string {
  return filename.replace(/[^a-zA-Z0-9_./-]/g, '_');
}

interface DocumentsStepProps {
  siteId: string;
  session: CoverageSession;
  /** Refresh the session record (documents list feeds the checklist table). */
  refetchSession: () => void;
  onAdvance: () => void;
}

/**
 * Step 2 — "Documents": upload and track coverage. Uploads go through the
 * two-step SAS uploader under the onboarding tenant, then one workflow run
 * classifies the batch; the checklist below polls at 10s and reflects
 * classification results as they land. "Submit for processing" stays
 * disabled until every Required row is Satisfied or Bypassed.
 */
export default function DocumentsStep({
  siteId,
  session,
  refetchSession,
  onAdvance,
}: DocumentsStepProps) {
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [running, setRunning] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [gate, setGate] = useState<GatePayload | null>(null);
  const [reviewItem, setReviewItem] = useState<ChecklistItem | null>(null);
  const [bypassTarget, setBypassTarget] = useState<ChecklistItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { checklist, loading, error, sessionGone, refetch } = useCoverageChecklist(
    siteId,
    session.session_id,
    true
  );

  // Each checklist poll also refreshes the session record silently, so newly
  // registered documents appear in the table without a manual reload.
  useEffect(() => {
    if (checklist) refetchSession();
  }, [checklist, refetchSession]);

  const heldForReview = session.documents.filter(doc => doc.lifecycle === 'needs_review');

  const actor = getUserData()?.email ?? 'unknown';

  const streamHandlers = useCallback(
    () => ({
      onSessionId: (id: string) => setConversationId(id),
      onGate: (gatePayload: GatePayload) => setGate(gatePayload),
      onError: (message: string) => setActionError(message),
      onDone: () => {
        setRunning(false);
        refetch();
        refetchSession();
      },
    }),
    [refetch, refetchSession]
  );

  const processFiles = async () => {
    const pending = files.filter(entry => entry.status === 'pending');
    if (pending.length === 0) return;
    setActionError(null);
    setRunning(true);

    const submissionId = crypto.randomUUID();
    const uploaded: OnboardingFilePayload[] = [];

    for (const entry of pending) {
      const index = files.indexOf(entry);
      const key = `${submissionId}/${sanitizeKey(entry.file.name)}`;
      setFiles(prev => prev.map((f, i) => (i === index ? { ...f, status: 'uploading' } : f)));
      try {
        await uploadStoreObject(
          key,
          entry.file,
          {},
          progress =>
            setFiles(prev =>
              prev.map((f, i) => (i === index ? { ...f, progress: progress.percentage } : f))
            ),
          undefined,
          ONBOARDING_TENANT
        );
        setFiles(prev =>
          prev.map((f, i) => (i === index ? { ...f, progress: 100, status: 'done' } : f))
        );
        uploaded.push({
          uri: `mcp-object-store://${key}`,
          filename: entry.file.name,
          content_type: entry.file.type || 'application/pdf',
          size_bytes: entry.file.size,
        });
      } catch (err) {
        setFiles(prev => prev.map((f, i) => (i === index ? { ...f, status: 'error' } : f)));
        setActionError(`Failed to upload ${entry.file.name}: ${(err as Error).message}`);
        setRunning(false);
        return;
      }
    }

    await streamOnboardingRun({ site_id: siteId, files: uploaded }, streamHandlers());
  };

  const answerGate = async (
    gatePayload: GatePayload,
    selectedOptionId: string,
    docType: string
  ) => {
    setGate(null);
    if (!conversationId) return;
    setRunning(true);
    await streamOnboardingResume(
      siteId,
      conversationId,
      {
        gate_id: gatePayload.gate_id,
        workflow_instance_id: gatePayload.workflow_instance_id,
        file_id: gatePayload.file_id,
        operator_response: {
          response_type: 'candidate_selected',
          selected_option_id: selectedOptionId,
          selected_doc_type: docType,
        },
      },
      streamHandlers()
    );
  };

  const doBypass = async (item: ChecklistItem, reason: string) => {
    setBypassTarget(null);
    try {
      await bypassItem(siteId, session.session_id, item.item_id, reason, actor);
      setActionError(null);
    } catch (err) {
      setActionError((err as Error).message);
    }
    refetch();
  };

  const doRemoveBypass = async (item: ChecklistItem) => {
    try {
      await removeBypass(siteId, session.session_id, item.item_id, actor);
      setActionError(null);
    } catch (err) {
      setActionError((err as Error).message);
    }
    refetch();
  };

  const doSubmit = async () => {
    setSubmitting(true);
    try {
      await submitSession(siteId, session.session_id);
      onAdvance();
    } catch (err) {
      setActionError((err as Error).message);
      refetch();
    } finally {
      setSubmitting(false);
    }
  };

  const requiredOpen = checklist?.rollup.required_open ?? 1;

  if (sessionGone) {
    return (
      <div className="obw-step">
        <ActionableNotification
          kind="warning"
          title="This onboarding session is no longer active"
          subtitle="The session ended on the server. Reload to start over; previously uploaded files remain in storage."
          lowContrast
          actionButtonLabel="Reload"
          onActionButtonClick={() => window.location.reload()}
          hideCloseButton
        />
      </div>
    );
  }

  return (
    <div className="obw-step">
      {(actionError || error) && (
        <InlineNotification
          kind="error"
          title="Coverage action failed"
          subtitle={actionError ?? error ?? ''}
          lowContrast
          onCloseButtonClick={() => setActionError(null)}
        />
      )}

      {heldForReview.length > 0 && (
        <InlineNotification
          kind="info"
          title={`${heldForReview.length} document${heldForReview.length === 1 ? '' : 's'} held for review`}
          subtitle={
            'Bundled or ambiguous uploads are held until reviewed and do not count toward the ' +
            'checklist yet: ' +
            heldForReview.map(doc => doc.filename ?? doc.document_id).join(', ')
          }
          lowContrast
          hideCloseButton
        />
      )}

      <UploadDropzone
        files={files}
        disabled={running}
        onAddFiles={added =>
          setFiles(prev => [
            ...prev,
            ...added.map(file => ({ file, progress: 0, status: 'pending' as const })),
          ])
        }
        onRemoveFile={index => setFiles(prev => prev.filter((_, i) => i !== index))}
      />
      <div className="obw-step-actions">
        <Button
          disabled={running || files.every(entry => entry.status !== 'pending')}
          onClick={processFiles}
        >
          Upload and classify
        </Button>
        {running && <InlineLoading description="Processing documents..." />}
      </div>

      {checklist && (
        <>
          <ChecklistStatTiles rollup={checklist.rollup} />
          <h4 className="obw-table-title">Document checklist</h4>
          <ChecklistTable
            items={checklist.items}
            documents={session.documents}
            provisionalEquipment={checklist.provisional_equipment}
            onBypass={setBypassTarget}
            onRemoveBypass={doRemoveBypass}
            onReviewMatch={setReviewItem}
          />
          <p className="obw-rules-note">
            Bypass always requires a reason, captured with your name. A bypassed item reopens
            automatically if a matching document arrives later. Submit stays disabled until every
            Required row is Satisfied or Bypassed; Recommended rows never block. Rows appear or
            regroup live as classification results arrive.
          </p>
        </>
      )}
      {loading && !checklist && <InlineLoading description="Loading checklist..." />}

      <div className="obw-step-actions">
        <Button disabled={requiredOpen > 0 || submitting || running} onClick={doSubmit}>
          Submit for processing
        </Button>
      </div>

      <BypassModal item={bypassTarget} onSubmit={doBypass} onClose={() => setBypassTarget(null)} />
      <ReviewMatchModal
        gate={gate}
        item={reviewItem}
        documents={session.documents}
        onAnswerGate={answerGate}
        onBypass={itemToBypass => {
          setReviewItem(null);
          setBypassTarget(itemToBypass);
        }}
        onClose={() => {
          setGate(null);
          setReviewItem(null);
        }}
      />
    </div>
  );
}
